export type Dir = 'up' | 'down' | 'left' | 'right';
export type Phase = 'ready' | 'playing' | 'gameover';
export type FoodKind = 'red' | 'golden';
export type DeathReason = 'wall' | 'self' | 'obstacle';

export interface Cell {
  x: number;
  y: number;
}

export interface Food {
  cell: Cell;
  kind: FoodKind;
  ttlMs: number | null;
}

export interface BoostPickup {
  cell: Cell;
}

export interface PortalPair {
  a: Cell;
  b: Cell;
  ttlMs: number;
  usesLeft: number;
}

export type SimEvent =
  | { type: 'eat'; kind: FoodKind; bonus: boolean }
  | { type: 'boost' }
  | { type: 'portal' }
  | { type: 'die'; reason: DeathReason }
  | { type: 'golden-gone' };

export interface SimConfig {
  goldenChance: number;
  goldenTtlMs: number;
  boostChance: number;
  boostMs: number;
  boostBonus: number;
  boostMinLength: number;
  portalChance: number;
  portalTtlMs: number;
  portalUses: number;
  portalImmunityTicks: number;
  portalMinLength: number;
  portalMinDistance: number;
  firstObstacleLength: number;
  obstacleEvery: number;
  obstacleCap: number;
  obstaclesPerWave: number;
  startSpeed: number;
  speedPerApple: number;
  maxSpeed: number;
  redScore: number;
  goldenScore: number;
  redGrow: number;
  goldenGrow: number;
  startLength: number;
}

export const DEFAULT_CONFIG: SimConfig = {
  goldenChance: 0.12,
  goldenTtlMs: 7000,
  boostChance: 0.018,
  boostMs: 5000,
  boostBonus: 10,
  boostMinLength: 6,
  portalChance: 0.012,
  portalTtlMs: 12000,
  portalUses: 2,
  portalImmunityTicks: 2,
  portalMinLength: 8,
  portalMinDistance: 6,
  firstObstacleLength: 8,
  obstacleEvery: 4,
  obstacleCap: 10,
  obstaclesPerWave: 2,
  startSpeed: 3.2,
  speedPerApple: 0.07,
  maxSpeed: 8,
  redScore: 10,
  goldenScore: 50,
  redGrow: 1,
  goldenGrow: 3,
  startLength: 3,
};

export const DIRS: Record<Dir, Cell> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

export const OPPOSITE: Record<Dir, Dir> = {
  up: 'down',
  down: 'up',
  left: 'right',
  right: 'left',
};

export interface GameState {
  cols: number;
  rows: number;
  snake: Cell[];
  prevSnake: Cell[];
  dir: Dir;
  queuedDir: Dir | null;
  pendingGrow: number;
  score: number;
  food: Food;
  boost: BoostPickup | null;
  boostMs: number;
  obstacles: Cell[];
  portals: PortalPair | null;
  portalImmunity: number;
  applesEaten: number;
  nextObstacleAtLength: number;
  phase: Phase;
  death: DeathReason | null;
  baseSpeed: number;
  moveAccumMs: number;
  rng: () => number;
  config: SimConfig;
  events: SimEvent[];
}

export const COLORS = {
  grassA: '#a8d44a',
  grassB: '#b8e05a',
  snake: '#4d7cff',
  snakeDark: '#3b66e0',
  snakeShadow: 'rgba(30, 58, 95, 0.22)',
  apple: '#e53935',
  appleDark: '#c62828',
  leaf: '#7cb342',
  stem: '#5d4037',
  gold: '#f5c400',
  goldDark: '#d4a017',
  boost: '#ffd54f',
  obstacle: '#c0754a',
  obstacleTop: '#d89060',
  portalA: '#26c6da',
  portalB: '#ec407a',
  ink: '#1e3a5f',
};

export const BEST_KEY = 'snake-best';
